import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, identity } from '../lib/api'
import { useCountdown, useLive } from '../lib/useLive'
import type { Slide } from '../lib/types'
import { Bars, WordCloud } from '../components/Results'
import { Announce, Brand, Leaders, Offline, PhaseBadge, Progress, Timer, Toast, ThemeToggle } from '../components/Bits'
import { people, points } from '../lib/plural'
import { GOAL, reachGoal } from '../lib/metrika'
import { useSeo } from '../lib/seo'
import { seriesOf, seriesStyle } from '../lib/series'

/** Экран участника: телефон в руке, поэтому всё крупное и в одну колонку. */
export default function Play() {
  const code = (useParams().code ?? '').toUpperCase()
  const [pid, setPid] = useState<string | null>(() => identity.pid(code))
  const [name, setName] = useState('')
  const [joinError, setJoinError] = useState<string | null>(null)
  const [editingName, setEditingName] = useState(false)

  useSeo({ title: `Эфир ${code} — clapme`, noindex: true })

  // Представляемся один раз: дальше личность живёт в localStorage, и обновление
  // страницы не превращает участника в нового.
  useEffect(() => {
    if (pid) return
    let cancelled = false
    api
      .join(code, { name: identity.lastName() || undefined })
      .then((joined) => {
        if (cancelled) return
        identity.remember(code, joined.participantId, joined.name)
        setPid(joined.participantId)
        setName(joined.name)
        reachGoal(GOAL.sessionJoined)
      })
      .catch((e) => setJoinError(e instanceof Error ? e.message : 'Эфир не найден'))
    return () => {
      cancelled = true
    }
  }, [code, pid])

  const live = useLive({ role: 'play', code, participantId: pid })
  const { state, results, me, leaderboard, error, send: sendRaw, reconnecting, welcome } = live

  // Первый ответ участника в эфире — цель Метрики. Считаем на отправке, а не на
  // отрисовке результата: так в одну цель попадают все три способа ответить —
  // опрос, свободный текст и тапы по эмодзи.
  const answeredOnce = useRef(false)
  const send = useCallback(
    (msg: Record<string, unknown>) => {
      if (!answeredOnce.current && (msg.t === 'answer' || msg.t === 'text' || msg.t === 'tap')) {
        answeredOnce.current = true
        reachGoal(GOAL.participantAnswered)
      }
      sendRaw(msg)
    },
    [sendRaw],
  )

  const slide = state?.slide ?? null
  const msLeft = useCountdown(state?.msLeft)

  // Имя берём у сервера, а не из localStorage: при повторном заходе вход по REST
  // не выполняется, и локального имени может просто не быть. Пока участник его
  // правит, серверное эхо не применяем — иначе оно перебивало бы набранный текст.
  useEffect(() => {
    if (!welcome?.name || editingName) return
    setName(welcome.name)
    identity.remember(code, welcome.participantId, welcome.name)
  }, [welcome, editingName, code])

  /**
   * Имя уходит на сервер, когда человек закончил печатать.
   *
   * Раньше отправка шла на каждое нажатие клавиши, а каждое переименование
   * вызывает рассылку списка зала всем пультам: одно имя из двадцати букв —
   * двадцать рассылок. На экране имя меняется сразу, на сервер уходит одно.
   *
   * Хук объявлен до ранних return: иначе при появлении ошибки входа число
   * хуков между рендерами менялось бы и экран участника падал целиком.
   */
  const renameTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(renameTimer.current), [])

  if (joinError) {
    return (
      <div className="wrap col">
        <Brand />
        <div className="card center col">
          <h2 style={{ margin: 0 }}>{joinError}</h2>
          <p className="muted" style={{ margin: 0 }}>
            Проверьте код на экране — возможно, эфир уже закончился.
          </p>
        </div>
      </div>
    )
  }

  const rename = (value: string, immediate = false) => {
    setName(value)
    const trimmed = value.trim()
    if (!trimmed) return

    window.clearTimeout(renameTimer.current)
    const push = () => {
      send({ t: 'rename', name: trimmed })
      if (pid) identity.remember(code, pid, trimmed)
    }

    if (immediate) push()
    else renameTimer.current = window.setTimeout(push, RENAME_DELAY_MS)
  }

  return (
    <div className="wrap col play-screen" style={{ gap: 20, maxWidth: 560 }}>
      <Offline reconnecting={reconnecting} />

      <header className="row spread">
        <Brand />
        {/* Код внутри эфира почти не нужен — он пригодится, только если сосед
            спросит «а какой код?». Поэтому тихо, мелко и без рамки. */}
        <div className="row" style={{ gap: 10 }}>
          <span className="muted tiny" style={{ letterSpacing: '0.18em' }}>{code}</span>
          <ThemeToggle />
        </div>
      </header>

      {/* Имя — строкой под шапкой, а не карточкой: его меняют раз за эфир,
          и лучшее место экрана ему ни к чему. */}
      <div className="row spread">
        {editingName ? (
          <input
            autoFocus
            value={name}
            maxLength={24}
            aria-label="Ваше имя"
            onChange={(e) => rename(e.target.value)}
            onBlur={(e) => {
              // Закончил править — отправляем сразу, не дожидаясь паузы.
              rename(e.target.value, true)
              setEditingName(false)
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              rename((e.target as HTMLInputElement).value, true)
              setEditingName(false)
            }}
          />
        ) : (
          <button className="link-button play-name" onClick={() => setEditingName(true)}>
            <strong>{name || '…'}</strong> <span className="muted tiny">изменить</span>
          </button>
        )}
        {(me?.score ?? 0) > 0 && (
          <span className="badge badge-live" data-testid="score">
            {points(me?.score ?? 0)}
          </span>
        )}
      </div>

      {state?.ended && (
        <div className="card center col">
          <h2 style={{ margin: 0 }}>Эфир завершён</h2>
          <p className="muted" style={{ margin: 0 }}>Спасибо, что участвовали!</p>
          <Leaders rows={leaderboard} title="Итоги" limit={5} meId={pid} />
        </div>
      )}

      {!state?.ended && !slide && (
        <div className="card center col grow" style={{ justifyContent: 'center' }}>
          <div style={{ fontSize: 56 }}>👋</div>
          <h2 style={{ margin: 0 }}>Вы в эфире</h2>
          <p className="muted" style={{ margin: 0 }}>Ждём, пока ведущий откроет первый вопрос</p>
          {/* Живой счётчик зала: он же подтверждает, что связь работает —
              иначе пустой экран ожидания неотличим от зависшего. */}
          {(state?.participants ?? 0) > 0 && (
            <p className="muted tiny" style={{ margin: 0 }}>
              Уже здесь: {people(state!.participants)}
            </p>
          )}
        </div>
      )}

      {!state?.ended && slide && (
        <div className="col grow" style={{ gap: 16 }}>
          {/* Служебное — сверху, вопрос с ответами — у нижнего края, под палец. */}
          <div className="play-status">
          <Progress index={state!.index} total={state!.total} />
          <div className="row spread row-wrap">
            <PhaseBadge phase={slide.phase} kind={slide.kind} />
            <Announce>
              {slide.phase === 'OPEN'
                ? `${slide.title}. Можно отвечать`
                : slide.phase === 'REVEALED'
                  ? 'Показан результат'
                  : slide.phase === 'CLOSED'
                    ? 'Приём ответов закрыт'
                    : slide.title}
            </Announce>
            <Timer msLeft={msLeft} totalSeconds={slide.timerSeconds} />
          </div>
          </div>

          <h1 className="play-title">{slide.title}</h1>

          <SlideInput
            slide={slide}
            me={me}
            send={send}
            answered={state!.answered}
            participants={state!.participants}
            tooFast={error?.code === 'too_fast'}
          />

          {results?.revealed && (
            <>
              {/* Вердикт — первым и крупно: для участника это главный момент
                  слайда, а раньше он был строчкой под диаграммой. */}
              {me?.awarded != null && me.awarded > 0 && (
                <div className="verdict verdict-ok">
                  <span>Верно!</span>
                  <strong>+{me.awarded}</strong>
                </div>
              )}
              {slide.scored && me?.awarded === 0 && (
                <div className="verdict">
                  <span>Не в этот раз</span>
                </div>
              )}
              <div className="card col" style={{ gap: 20 }}>
                {results.view === 'BARS' && <Bars bars={results.bars} revealed />}
                {results.view === 'CLOUD' && <WordCloud items={results.cloud} />}
                <Leaders rows={leaderboard} limit={5} meId={pid} />
              </div>
            </>
          )}
        </div>
      )}

      <Toast message={error?.message ?? null} />
    </div>
  )
}

/** Ввод ответа. Каждый тип слайда — своя механика, но состояние всегда одно: «ответил/нет». */
function SlideInput({
  slide,
  me,
  send,
  answered,
  participants,
  tooFast,
}: {
  slide: Slide
  me: ReturnType<typeof useLive>['me']
  send: (msg: Record<string, unknown>) => void
  answered: number
  participants: number
  /** Сервер отбил последнее сообщение как слишком частое. */
  tooFast: boolean
}) {
  const open = slide.phase === 'OPEN'

  // Пока ждём результат, глухое «ждём» превращается в понятное ожидание:
  // видно, что эфир жив и сколько людей уже ответило.
  const answeredOf = answered > 0 ? `Ответили ${answered} из ${participants}` : null

  /**
   * Свой выбор держим локально и не ждём подтверждения сервера.
   *
   * Иначе при мультиселекте два быстрых тапа подряд теряют первый: ответ с
   * актуальным набором идёт до 340 мс (сеть плюс троттлинг рассылки), и второй
   * тап успевает отправиться, ничего не зная о первом. На localhost это не
   * видно, на мобильной сети — ломается сразу.
   */
  const [localPicked, setLocalPicked] = useState<Set<string> | null>(null)
  useEffect(() => setLocalPicked(null), [slide.id, slide.phase])

  const serverPicked = useMemo(() => new Set(me?.picked ?? []), [me?.picked])
  const picked = localPicked ?? serverPicked

  // Сервер отбил сообщение как слишком частое — значит наш локальный выбор
  // разошёлся с тем, что на сервере. Показывать расхождение нельзя: возвращаем
  // экран к серверной правде.
  useEffect(() => {
    if (tooFast) setLocalPicked(null)
  }, [tooFast])

  // Телефон в руке: подтверждение должно чувствоваться, а не только читаться.
  const wasAnswered = useRef(false)
  useEffect(() => {
    const answered = me?.answered === true
    if (answered && !wasAnswered.current && navigator.vibrate) navigator.vibrate(14)
    wasAnswered.current = answered
  }, [me?.answered])

  /**
   * Ответ уходит сразу, без придерживания.
   *
   * Придерживать его нельзя: голосование закрывают ровно в тот момент, когда
   * люди тапают чаще всего — в последнюю секунду, — и отложенный ответ там
   * просто потеряется. От злонамеренного потока это всё равно не защищает:
   * скрипт шлёт мимо интерфейса, и режет его ограничение на сервере.
   *
   * Объявлен до ранних return по типу слайда: хуки нельзя вызывать после них.
   * Когда ведущий переключался с опроса на эмодзи, число хуков менялось между
   * рендерами и экран участника падал целиком.
   */
  const pushAnswer = useCallback(
    (optionIds: string[]) => {
      send({ t: 'answer', slideId: slide.id, optionIds })
    },
    [send, slide.id],
  )

  if (slide.kind === 'EMOJI_STORM') {
    return <EmojiPad slide={slide} send={send} disabled={!open} />
  }

  if (slide.kind === 'FREE_TEXT') {
    return <TextInput slide={slide} me={me} send={send} disabled={!open} />
  }

  const multi = slide.kind === 'POLL_MULTI'
  const correctIds = new Set(me?.correctIds ?? [])
  const revealed = slide.phase === 'REVEALED'

  const toggle = (optionId: string) => {
    if (!open) return
    if (multi) {
      const next = new Set(picked)
      if (next.has(optionId)) next.delete(optionId)
      else next.add(optionId)
      setLocalPicked(next)
      // Пустой набор шлём тоже: для сервера это отзыв ответа. Иначе снятая
      // галочка оставалась бы голосом в диаграмме, хотя участник её уже снял.
      pushAnswer([...next])
    } else {
      setLocalPicked(new Set([optionId]))
      pushAnswer([optionId])
    }
  }

  return (
    <div className="col" style={{ gap: 12 }}>
      <div className="options">
        {slide.options.map((option, index) => {
          const isPicked = picked.has(option.id)
          const isCorrect = revealed && correctIds.has(option.id)
          const isWrong = revealed && isPicked && !correctIds.has(option.id)
          return (
            <button
              key={option.id}
              className={`option${isPicked ? ' option-picked' : ''}${isCorrect ? ' option-correct' : ''}${
                isWrong ? ' option-wrong' : ''
              }`}
              disabled={!open}
              // Выбор передаётся не только цветом и значком: без aria-pressed
              // со скринридера не отличить отмеченный вариант от обычного.
              aria-pressed={isPicked}
              style={seriesStyle(index)}
              onClick={() => toggle(option.id)}
            >
              {/* Буква та же, что у строки на проекторе. Скринридеру она не нужна:
                  выбор объявляет aria-pressed, а вариант — его подпись. */}
              <span className="option-mark" aria-hidden>
                {isCorrect ? '✓' : isWrong ? '✕' : seriesOf(index).letter}
              </span>
              <span>{option.text}</span>
            </button>
          )
        })}
      </div>
      {multi && open && <p className="play-note">Можно выбрать несколько — ответ сохраняется сразу</p>}
      {/* «Закрыто» и «ещё не открывали» — разные вещи: на неначатом слайде
          сообщение о закрытии сбивает с толку. */}
      {slide.phase === 'IDLE' && (
        <p className="play-note">Ведущий ещё не открыл голосование</p>
      )}
      {slide.phase === 'CLOSED' && (
        <p className="play-note">
          Голосование закрыто, ждём результат
          {answeredOf && <><br />{answeredOf}</>}
        </p>
      )}
      {open && me?.answered && <p className="play-note play-note-ok">Ответ принят — можно поменять</p>}
    </div>
  )
}

/**
 * Эмодзи-пад.
 *
 * Одна реакция в секунду от человека — столько же принимает сервер
 * (LiveSession.MAX_TAPS_PER_SECOND). Лимит держит сервер, но показать его обязан
 * телефон: иначе на своём экране участник видит полёт каждого тапа, а до стены
 * долетает один из десяти, и пад выглядит рабочим, не будучи им.
 *
 * Поэтому после тапа пад гаснет на [TAP_COOLDOWN_MS] и под ним идёт отсчёт.
 */
function EmojiPad({
  slide,
  send,
  disabled,
}: {
  slide: Slide
  send: (msg: Record<string, unknown>) => void
  disabled: boolean
}) {
  const [sparks, setSparks] = useState<{ key: number; emoji: string; shift: number }[]>([])
  // Свои реакции улетают вверх прямо на телефоне — тем же движением, что и на
  // большом экране. Это и заполняет пустоту над падом, и связывает телефон
  // с проектором: видно, что твой тап улетел именно туда.
  const [mine, setMine] = useState<{ key: number; emoji: string; left: number; dur: number }[]>([])
  const nextSpark = useRef(0)

  // Пад закрыт до этого момента. Держим отметку времени, а не просто флаг:
  // по ней же считается длина полоски отсчёта, и она переживает перерисовки.
  const [coolingKey, setCoolingKey] = useState(0)
  const [cooling, setCooling] = useState(false)
  const coolTimer = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(coolTimer.current), [])

  const tap = (emoji: string) => {
    if (disabled || cooling) return

    // Раньше тапы копились и уходили пачкой раз в 400 мс — это экономило
    // сообщения, когда по кнопке били без счёта. Теперь тап в секунду один,
    // копить нечего, а лишние 400 мс задержки чувствуются в руке.
    send({ t: 'tap', slideId: slide.id, emoji, n: 1 })

    setCooling(true)
    setCoolingKey((n) => n + 1)
    window.clearTimeout(coolTimer.current)
    coolTimer.current = window.setTimeout(() => setCooling(false), TAP_COOLDOWN_MS)

    // Своя реакция должна быть видна мгновенно: пока тап летит на сервер и
    // возвращается дельтой на проектор, проходит до 550 мс, и без локального
    // отклика кнопка кажется мёртвой.
    const key = nextSpark.current++
    setSparks((current) => [
      ...current.slice(-14),
      { key, emoji, shift: Math.round((Math.random() - 0.5) * 70) },
    ])
    window.setTimeout(() => setSparks((current) => current.filter((s) => s.key !== key)), 800)

    const dur = 1.8 + Math.random() * 0.9
    setMine((current) => [
      ...current.slice(-18),
      { key, emoji, left: 8 + Math.random() * 84, dur },
    ])
    window.setTimeout(() => setMine((current) => current.filter((f) => f.key !== key)), dur * 1000)

    if (navigator.vibrate) navigator.vibrate(8)
  }

  return (
    <div className="col grow emoji-zone" style={{ gap: 12 }}>
      <div className="emoji-mine" aria-hidden>
        {mine.map((fly) => (
          <span
            key={fly.key}
            className="emoji-fly"
            style={{ left: `${fly.left}%`, ['--dur' as string]: `${fly.dur}s` }}
          >
            {fly.emoji}
          </span>
        ))}
      </div>

      <div className="emoji-pad">
        {slide.emojiSet.map((emoji) => {
          const mine = sparks.filter((s) => s.emoji === emoji)
          return (
            <button
              key={emoji}
              className={`emoji-key${mine.length > 0 ? ' emoji-key-hit' : ''}`}
              disabled={disabled || cooling}
              aria-label={`Отправить реакцию ${emoji}`}
              onClick={() => tap(emoji)}
            >
              {emoji}
              {mine.map((spark) => (
                <span
                  key={spark.key}
                  className="emoji-spark"
                  style={{ ['--sx' as string]: `${spark.shift}px` }}
                  aria-hidden
                >
                  {emoji}
                </span>
              ))}
            </button>
          )
        })}
      </div>
      {/* Полоска добегает до конца — можно снова. Высота у дорожки есть всегда,
          иначе появление полоски дёргало бы пад вверх-вниз на каждом тапе. */}
      <div className="emoji-cool" aria-hidden>
        {cooling && <i key={coolingKey} style={{ animationDuration: `${TAP_COOLDOWN_MS}ms` }} />}
      </div>
      <p className="play-note">
        {disabled ? 'Пока закрыто' : 'Реакции летят на большой экран — по одной в секунду'}
      </p>
    </div>
  )
}

/**
 * Пауза между реакциями одного участника.
 *
 * Чуть больше секунды, хотя сервер принимает ровно одну в секунду: телефон
 * должен отставать от сервера, а не обгонять его. Совпадай они секунда в
 * секунду, дрожание сети иногда приносило бы тап на пару миллисекунд раньше
 * срока, и он молча пропадал бы — участник видел бы свой полёт на телефоне,
 * а на стене его реакции не было.
 */
const TAP_COOLDOWN_MS = 1100

/** Пауза, после которой имя считается дописанным. */
const RENAME_DELAY_MS = 600

/**
 * Пауза кнопки после отправки текста.
 *
 * Не защита от спама — её держит сервер, — а понятное поведение: без неё
 * частое нажатие упиралось бы в отказ вместо видимой паузы.
 */
const SEND_COOLDOWN_MS = 700

function TextInput({
  slide,
  me,
  send,
  disabled,
}: {
  slide: Slide
  me: ReturnType<typeof useLive>['me']
  send: (msg: Record<string, unknown>) => void
  disabled: boolean
}) {
  const [value, setValue] = useState('')
  const sent = me?.text ?? null

  // После отправки кнопка ненадолго гаснет: на сервере тот же предел, и без
  // этого частое нажатие упиралось бы в отказ вместо понятной паузы.
  const [justSent, setJustSent] = useState(false)

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed || disabled || justSent) return
    send({ t: 'text', slideId: slide.id, text: trimmed })
    setValue('')
    setJustSent(true)
    window.setTimeout(() => setJustSent(false), SEND_COOLDOWN_MS)
  }

  return (
    <div className="col" style={{ gap: 10 }}>
      {sent && (
        <div className="card row spread" style={{ padding: 12 }}>
          <span>Ваш ответ: <strong>{sent}</strong></span>
          {!disabled && <span className="muted tiny">можно переписать</span>}
        </div>
      )}
      <textarea
        rows={3}
        maxLength={slide.maxTextLength}
        placeholder={disabled ? 'Приём ответов закрыт' : 'Напишите ответ'}
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <div className="row spread">
        <span className="muted tiny">
          {value.length} / {slide.maxTextLength}
        </span>
        <button
          className="btn btn-primary"
          onClick={submit}
          disabled={disabled || !value.trim() || justSent}
        >
          Отправить
        </button>
      </div>
    </div>
  )
}
